import React, { useEffect, useRef, useState } from 'react';
import { Layout, Card, Table, Input, Space, Typography, Spin, Alert, Select, DatePicker, InputNumber, Button, Dropdown, Menu } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ScatterChart, Scatter } from 'recharts';
import { getCsvFromS3, getJsonFromS3 } from '../utils/s3Utils';
import dayjs from 'dayjs';
import html2canvas from 'html2canvas';

const { Content } = Layout;
const { Title } = Typography;
const { RangePicker } = DatePicker;

interface FileData {
  fileName: string;
  data: any[];
  originalData: any[];
  columns: any[];
  chartData: any[];
  chartType: 'bar' | 'area' | 'pie';
  loading: boolean;
  error: string | null;
  filters: { [key: string]: any };
  isJson?: boolean;
  isJsonL?: boolean;
  // added for JSON text vs time scatter
  textTimeData?: any[];
}

interface DataRow {
  [key: string]: string | number | undefined;
}

const UC1: React.FC = () => {
  const targetFiles = [
    'test_output_forecast.csv',
    'test_output_aggregate.csv',
    'test_output_clustering.csv',
    'output_call_centers.json',
    'nova-tweets-2022-11_12-influential-users-by-followers-top-20.jsonl'
  ];

  const chartRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Explicitly type the state so TS knows this is always FileData[]
  const initialFiles: FileData[] = targetFiles.map(name => ({
    fileName: name,
    data: [],
    originalData: [],
    columns: [],
    chartData: [],
    chartType: name.endsWith('.json') ? 'pie' : 'bar',
    loading: true,
    error: null,
    filters: {},
    isJson: name.endsWith('.json'),
    isJsonL: name.endsWith('.jsonl'),
    textTimeData: []
  }));

  const [allFilesData, setAllFilesData] = useState<FileData[]>(initialFiles);

  useEffect(() => {
    const loadAllFiles = async () => {
      const updatedFiles: FileData[] = [...initialFiles];

      await Promise.all(
        targetFiles.map(async (fileName, index) => {
          try {
            let rows: any[] = [];
            let newColumns: any[] = [];
            let chartType: 'bar' | 'area' | 'pie' = 'bar';
            let chartData: any[] = [];
            let textTimeData: any[] = [];

            if (fileName.endsWith('.json') || fileName.endsWith('.jsonl')) {
              console.log(`Loading JSON file: ${fileName}`);
              const jsonData = await getJsonFromS3(fileName);
              console.log('Loaded JSON data:', jsonData);

              rows = Array.isArray(jsonData) ? jsonData : (jsonData ? [jsonData] : []);

              // Handle different JSON structures based on file type
              if (fileName === 'output_call_centers.json') {
                // Call center data structure
                newColumns = [
                  { title: 'Analysis Result', dataIndex: 'analysis_result', key: 'analysis_result' },
                  { title: 'Confidence Score', dataIndex: 'confidence', key: 'confidence', render: (value: number) => `${(value * 100).toFixed(2)}%` },
                  { title: 'Text Preview', dataIndex: 'textPreview', key: 'textPreview' },
                  { title: 'Processing Time', dataIndex: 'time', key: 'time', render: (value: number) => `${value.toFixed(3)}s` }
                ];

                const transformed = rows.map((item: any, idx: number) => {
                  const preds: number[] = Array.isArray(item.predictions) ? item.predictions.map((p: any) => Number(p)) : [];
                  const confidence = preds.length > 0 ? Math.max(...preds) : 0;
                  const text = typeof item.text === 'string' ? item.text : String(item.text ?? '');

                  return {
                    key: String(idx),
                    analysis_result: item.analysis_result ?? '',
                    confidence,
                    textPreview: text.substring(0, 100) + (text.length > 100 ? '...' : ''),
                    fullText: text,
                    time: Number(item.time ?? 0),
                    predictions: preds
                  };
                });

                chartType = 'pie';
                chartData = prepareCallCenterData(transformed);
                textTimeData = transformed.map(r => ({ time: Number(r.time ?? 0), length: (r.fullText ?? '').length, key: r.key }));

              } else if (fileName.endsWith('.jsonl')) {
                // JSONL data structure (like the tweets file)
                newColumns = [
                  { title: 'Position', dataIndex: 'position', key: 'position' },
                  { title: 'Screen Name', dataIndex: ['user', 'screen_name'], key: 'screen_name' },
                  { title: 'Name', dataIndex: ['user', 'name'], key: 'name' },
                  { title: 'Followers', dataIndex: 'score', key: 'score' },
                  { title: 'Location', dataIndex: ['user', 'location'], key: 'location' },
                  { title: 'Description', dataIndex: ['user', 'description'], key: 'description', ellipsis: true }
                ];

                const transformed = rows.map((item: any, idx: number) => ({
                  key: String(idx),
                  position: item.position ?? idx + 1,
                  user: item.user ?? {},
                  score: item.score ?? 0,
                  ...item
                }));

                chartType = 'bar';
                chartData = prepareJsonLData(transformed);
              }

              updatedFiles[index] = {
                fileName,
                data: rows,
                originalData: [...rows],
                columns: newColumns,
                chartData,
                chartType,
                loading: false,
                error: null,
                filters: {},
                isJson: fileName.endsWith('.json'),
                isJsonL: fileName.endsWith('.jsonl'),
                textTimeData
              };

            } else {
              // CSV handling (unchanged)
              const csvData = await getCsvFromS3(fileName);
              const parsed = Array.isArray(csvData) ? csvData : [];

              const firstRow = parsed[0] ?? {};
              newColumns = Object.keys(firstRow).map(key => ({ title: key, dataIndex: key, key }));

              const mappedData: DataRow[] = parsed.map((row: any, rIdx: number) => {
                const rowData: DataRow = {};
                newColumns.forEach(col => {
                  const val = row[col.dataIndex];
                  rowData[col.dataIndex] = val !== undefined && val !== null ? val : '';
                });
                return rowData;
              });

              // Determine chart type
              if (isClusteringData(newColumns)) {
                chartType = 'bar';
                chartData = prepareClusteringData(mappedData, newColumns);
              } else {
                chartType = 'area';
                chartData = prepareTimeSeriesData(mappedData, newColumns);
              }

              updatedFiles[index] = {
                fileName,
                data: mappedData,
                originalData: [...mappedData],
                columns: newColumns,
                chartData,
                chartType,
                loading: false,
                error: null,
                filters: {},
                isJson: false,
                isJsonL: false,
                textTimeData: []
              };
            }

          } catch (err) {
            console.error(`Error loading file ${fileName}:`, err);
            updatedFiles[index] = {
              fileName,
              data: [],
              originalData: [],
              columns: [],
              chartData: [],
              chartType: 'bar',
              loading: false,
              error: `Failed to load ${fileName}: ${err instanceof Error ? err.message : String(err)}`,
              filters: {},
              isJson: fileName.endsWith('.json'),
              isJsonL: fileName.endsWith('.jsonl'),
              textTimeData: []
            };
          }
        })
      );

      setAllFilesData(updatedFiles);
    };

    loadAllFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isClusteringData = (columns: any[]): boolean => {
    const clusterColumn = columns.find(col =>
      col.dataIndex === 'Cluster' || col.dataIndex === 'cluster'
    );
    return !!clusterColumn;
  };

  const prepareTimeSeriesData = (data: DataRow[], columns: any[]) => {
    const timestampCol = columns[0]?.dataIndex;
    const valueCol = columns[1]?.dataIndex;

    if (timestampCol && valueCol) {
      return data
        .map(row => ({
          time: String(row[timestampCol] ?? ''),
          value: Number(row[valueCol]) || 0
        }))
        .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
    }
    return [];
  };

  const prepareClusteringData = (data: DataRow[], columns: any[]) => {
    const clusterCol = columns.find(col =>
      col.dataIndex === 'Cluster' || col.dataIndex === 'cluster'
    );

    if (clusterCol) {
      const clusterCounts = data.reduce((acc: { name: string; count: number }[], row) => {
        const raw = row[clusterCol.dataIndex];
        const cluster = String(raw ?? 'Unknown');
        const existing = acc.find(item => item.name === cluster);
        if (existing) {
          existing.count++;
        } else {
          acc.push({ name: cluster, count: 1 });
        }
        return acc;
      }, []);

      console.log('Clustering data prepared:', clusterCounts);
      return clusterCounts;
    }
    return [];
  };

  const prepareCallCenterData = (data: any[]) => {
    const sentimentCounts = data.reduce((acc: { name: string; value: number }[], row: any) => {
      const sentiment = String(row.analysis_result ?? 'Unknown');
      const existing = acc.find(item => item.name === sentiment);
      if (existing) {
        existing.value++;
      } else {
        acc.push({ name: sentiment, value: 1 });
      }
      return acc;
    }, []);

    return sentimentCounts;
  };

  const prepareJsonLData = (data: any[]) => {
    // For JSONL files like tweets, create bar chart of followers by user
    return data.map(item => ({
      name: item.user?.screen_name || `User ${item.position}`,
      followers: item.score || 0,
      position: item.position
    })).sort((a, b) => b.followers - a.followers);
  };

  const handleFilterChange = (fileName: string, filterField: string, value: any) => {
    setAllFilesData(prevFiles => prevFiles.map(file => {
      if (file.fileName === fileName) {
        const newFilters = { ...file.filters, [filterField]: value };
        let filteredData = [...file.originalData];

        if (file.isJson || file.isJsonL) {
          Object.entries(newFilters).forEach(([field, fieldValue]) => {
            if (fieldValue) {
              if (file.fileName === 'output_call_centers.json') {
                // Call center JSON filters
                if (field === 'analysis_result') {
                  filteredData = filteredData.filter((row: any) =>
                    String(row.analysis_result ?? '').toLowerCase().includes(String(fieldValue).toLowerCase())
                  );
                } else if (field === 'confidenceRange') {
                  const [min, max] = fieldValue;
                  const minVal = min !== undefined ? Number(min) : -Infinity;
                  const maxVal = max !== undefined ? Number(max) : Infinity;

                  if (min !== undefined && max !== undefined && minVal > maxVal) {
                    return;
                  }

                  filteredData = filteredData.filter((row: any) => {
                    const conf = Number(row.confidence ?? 0);
                    return conf >= minVal / 100 && conf <= maxVal / 100;
                  });
                } else if (field === 'textSearch') {
                  filteredData = filteredData.filter((row: any) =>
                    String(row.fullText ?? '').toLowerCase().includes(String(fieldValue).toLowerCase())
                  );
                }
              } else if (file.isJsonL) {
                // JSONL filters (for tweets)
                if (field === 'screen_name') {
                  filteredData = filteredData.filter((row: any) =>
                    String(row.user?.screen_name ?? '').toLowerCase().includes(String(fieldValue).toLowerCase())
                  );
                } else if (field === 'position') {
                  filteredData = filteredData.filter((row: any) =>
                    String(row.position ?? '').includes(String(fieldValue))
                  );
                } else if (field === 'followersRange') {
                  const [min, max] = fieldValue;
                  const minVal = min !== undefined ? Number(min) : -Infinity;
                  const maxVal = max !== undefined ? Number(max) : Infinity;

                  if (min !== undefined && max !== undefined && minVal > maxVal) {
                    return;
                  }

                  filteredData = filteredData.filter((row: any) => {
                    const followers = Number(row.score ?? 0);
                    return followers >= minVal && followers <= maxVal;
                  });
                }
              }
            }
          });
        } else {
          // CSV filters (unchanged)
          const timestampCol = file.columns[0]?.dataIndex;
          const valueCol = file.columns[1]?.dataIndex;

          Object.entries(newFilters).forEach(([field, fieldValue]) => {
            if (fieldValue) {
              if (field === 'dateRange' && timestampCol && Array.isArray(fieldValue)) {
                const [start, end] = fieldValue;
                if (start && end) {
                  filteredData = filteredData.filter((row: any) => {
                    const time = dayjs(String(row[timestampCol] ?? ''));
                    return time.isAfter(start) && time.isBefore(end);
                  });
                }
              } else if (field === 'valueFilter' && valueCol && fieldValue) {
                const { operator, number } = fieldValue;
                if (operator && number !== undefined && number !== null) {
                  filteredData = filteredData.filter((row: any) => {
                    const val = Number(String(row[valueCol] ?? ''));
                    if (isNaN(val)) return false;
                    if (operator === 'eq') return val === number;
                    if (operator === 'gt') return val > number;
                    if (operator === 'lt') return val < number;
                    return true;
                  });
                }
              } else if (fieldValue) {
                filteredData = filteredData.filter((row: any) =>
                  String(row[field] ?? '').toLowerCase().includes(String(fieldValue).toLowerCase())
                );
              }
            }
          });
        }

        let newChartData: any[] = [];
        let newTextTimeData: any[] = [];

        if (file.isJsonL) {
          newChartData = prepareJsonLData(filteredData);
        } else if (file.chartType === 'pie') {
          newChartData = prepareCallCenterData(filteredData);
          newTextTimeData = (filteredData as any[]).map(r => ({ time: Number(r.time ?? 0), length: (r.fullText ?? '').length, key: r.key }));
        } else if (file.chartType === 'bar' && !file.isJson && !file.isJsonL) {
          newChartData = prepareClusteringData(filteredData as DataRow[], file.columns);
        } else {
          newChartData = prepareTimeSeriesData(filteredData as DataRow[], file.columns);
        }

        return { ...file, filters: newFilters, data: filteredData, chartData: newChartData, textTimeData: newTextTimeData };
      }
      return file;
    }));
  };

  const resetFilters = (fileName: string) => {
    setAllFilesData(prevFiles => prevFiles.map(file => {
      if (file.fileName === fileName) {
        let chartData: any[] = [];
        let textTimeData: any[] = [];

        if (file.isJsonL) {
          chartData = prepareJsonLData([...file.originalData]);
        } else if (file.chartType === 'pie') {
          chartData = prepareCallCenterData([...file.originalData]);
          textTimeData = (file.originalData as any[]).map(r => ({ time: Number(r.time ?? 0), length: (r.fullText ?? '').length, key: r.key }));
        } else if (file.chartType === 'bar') {
          chartData = prepareClusteringData([...file.originalData] as DataRow[], file.columns);
        } else {
          chartData = prepareTimeSeriesData([...file.originalData] as DataRow[], file.columns);
        }

        return { ...file, filters: {}, data: [...file.originalData], chartData, textTimeData };
      }
      return file;
    }));
  };

  // Export functions (unchanged)
  const exportAsJSON = (fileName: string, chartData: any[]) => {
    const dataStr = JSON.stringify(chartData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName.replace(/\.(csv|json|jsonl)$/, '')}_chart_data.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportAsCSV = (fileName: string, chartData: any[]) => {
    if (chartData.length === 0) return;

    const headers = Object.keys(chartData[0]);
    const csvContent = [
      headers.join(','),
      ...chartData.map(row => headers.map(header => `"${row[header] ?? ''}"`).join(','))
    ].join('\n');

    const dataBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName.replace(/\.(csv|json|jsonl)$/, '')}_chart_data.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportAsPNG = async (fileName: string) => {
    const chartElement = chartRefs.current[fileName];
    if (!chartElement) return;

    try {
      const canvas = await html2canvas(chartElement, { backgroundColor: '#ffffff', scale: 2 });
      const link = document.createElement('a');
      link.download = `${fileName.replace(/\.(csv|json|jsonl)$/, '')}_chart.png`;
      link.href = canvas.toDataURL();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting as PNG:', error);
      alert('Failed to export as PNG');
    }
  };

  const getExportMenu = (fileName: string, chartData: any[]) => (
    <Menu>
      <Menu.Item key="json" onClick={() => exportAsJSON(fileName, chartData)}>Export as JSON</Menu.Item>
      <Menu.Item key="csv" onClick={() => exportAsCSV(fileName, chartData)}>Export as CSV</Menu.Item>
      <Menu.Item key="png" onClick={() => exportAsPNG(fileName)}>Export as PNG</Menu.Item>
    </Menu>
  );

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  return (
    <Content style={{ padding: '24px' }}>
      <Title level={2}>UC1 - Data Visualization</Title>

      {allFilesData.every(file => file.loading) && (
        <div style={{ padding: '20px' }}>
          <Spin tip="Loading files..." size="large" />
        </div>
      )}

      {allFilesData.some(file => file.error) && (
        <Alert
          message="Error loading files"
          description={allFilesData.filter(f => f.error).map(f => f.error).join('\n')}
          type="error"
          showIcon
          closable
        />
      )}

      {allFilesData.map((fileData, index) => (
        <div key={index} className="mb-6">
          <Card title={`Data Table - ${fileData.fileName}`} style={{ marginBottom: '24px' }}>
            <Space style={{ marginBottom: 16 }} wrap>

              {/* Call Center JSON filters */}
              {fileData.fileName === 'output_call_centers.json' && (
                <>
                  <Select
                    placeholder="Filter by Analysis Result"
                    value={fileData.filters.analysis_result || undefined}
                    onChange={(value) => handleFilterChange(fileData.fileName, 'analysis_result', value)}
                    allowClear
                    style={{ width: 200 }}
                  >
                    {Array.from(new Set((fileData.originalData as any[]).map(row => String(row.analysis_result ?? ''))))
                      .map(val => (
                        <Select.Option key={val} value={val}>{val}</Select.Option>
                      ))}
                  </Select>

                  <Input
                    placeholder="Search in transcribed text"
                    value={fileData.filters.textSearch || ''}
                    onChange={(e) => handleFilterChange(fileData.fileName, 'textSearch', e.target.value)}
                    style={{ width: 200 }}
                  />

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>Confidence %:</span>
                    <InputNumber
                      placeholder="Min"
                      min={0}
                      max={100}
                      value={fileData.filters.confidenceRange?.[0] ?? null}
                      onChange={(val) => handleFilterChange(fileData.fileName, 'confidenceRange', [val, fileData.filters.confidenceRange?.[1]])}
                      style={{ width: 80 }}
                    />
                    <span>-</span>
                    <InputNumber
                      placeholder="Max"
                      min={0}
                      max={100}
                      value={fileData.filters.confidenceRange?.[1] ?? null}
                      onChange={(val) => handleFilterChange(fileData.fileName, 'confidenceRange', [fileData.filters.confidenceRange?.[0], val])}
                      style={{ width: 80 }}
                    />
                  </div>
                </>
              )}

              {/* JSONL filters (for tweets) */}
              {fileData.isJsonL && (
                <>
                  <Input
                    placeholder="Filter by Screen Name"
                    value={fileData.filters.screen_name || ''}
                    onChange={(e) => handleFilterChange(fileData.fileName, 'screen_name', e.target.value)}
                    style={{ width: 200 }}
                  />

                  <Input
                    placeholder="Filter by Position"
                    value={fileData.filters.position || ''}
                    onChange={(e) => handleFilterChange(fileData.fileName, 'position', e.target.value)}
                    style={{ width: 120 }}
                  />

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>Followers:</span>
                    <InputNumber
                      placeholder="Min"
                      min={0}
                      value={fileData.filters.followersRange?.[0] ?? null}
                      onChange={(val) => handleFilterChange(fileData.fileName, 'followersRange', [val, fileData.filters.followersRange?.[1]])}
                      style={{ width: 100 }}
                    />
                    <span>-</span>
                    <InputNumber
                      placeholder="Max"
                      min={0}
                      value={fileData.filters.followersRange?.[1] ?? null}
                      onChange={(val) => handleFilterChange(fileData.fileName, 'followersRange', [fileData.filters.followersRange?.[0], val])}
                      style={{ width: 100 }}
                    />
                  </div>
                </>
              )}

              {/* Time-series filters (for forecast & aggregate) */}
              {fileData.chartType === 'area' && !fileData.isJson && !fileData.isJsonL && (
                <>
                  <RangePicker
                    showTime
                    value={fileData.filters.dateRange || null}
                    onChange={(dates) => handleFilterChange(fileData.fileName, 'dateRange', dates)}
                  />

                  <Select
                    placeholder="Condition"
                    style={{ width: 160 }}
                    value={fileData.filters.valueFilter?.operator || undefined}
                    onChange={(op) => handleFilterChange(fileData.fileName, 'valueFilter', { ...fileData.filters.valueFilter, operator: op })}
                  >
                    <Select.Option value="eq">Equals</Select.Option>
                    <Select.Option value="gt">Greater Than</Select.Option>
                    <Select.Option value="lt">Less Than</Select.Option>
                  </Select>

                  <InputNumber
                    placeholder="Value"
                    value={fileData.filters.valueFilter?.number ?? null}
                    onChange={(val) => handleFilterChange(fileData.fileName, 'valueFilter', { ...fileData.filters.valueFilter, number: val })}
                  />
                </>
              )}

              {/* Clustering filters */}
              {fileData.chartType === 'bar' && !fileData.isJson && !fileData.isJsonL && (
                <>
                  <Input
                    placeholder="Filter by User ID"
                    value={fileData.filters['User ID'] || ''}
                    onChange={(e) => handleFilterChange(fileData.fileName, 'User ID', e.target.value)}
                    style={{ width: 200 }}
                  />

                  <Select
                    placeholder="Filter by Cluster"
                    value={fileData.filters['Cluster'] || undefined}
                    onChange={(value) => handleFilterChange(fileData.fileName, 'Cluster', value)}
                    allowClear
                    style={{ width: 150 }}
                  >
                    {Array.from(new Set((fileData.originalData as DataRow[]).map(row => String(row['Cluster'] ?? ''))))
                      .map(val => (
                        <Select.Option key={val} value={val}>{val}</Select.Option>
                      ))}
                  </Select>
                </>
              )}

              <Button onClick={() => resetFilters(fileData.fileName)}>Reset Filters</Button>
            </Space>

            <Table
              dataSource={fileData.data}
              columns={fileData.columns}
              rowKey={(record, idx) => String(idx ?? 0)}
              pagination={{ pageSize: 10, showSizeChanger: true }}
              expandable={fileData.fileName === 'output_call_centers.json' ? {
                expandedRowRender: (record: any) => (
                  <div style={{ margin: 0 }}>
                    <p><strong>Full Text:</strong></p>
                    <p style={{ padding: '8px', background: '#f5f5f5', borderRadius: '4px' }}>{record.fullText}</p>
                    <p><strong>Predictions:</strong> {Array.isArray(record.predictions) ? record.predictions.map((p: number, i: number) => `${i === 0 ? 'Positive' : 'Negative'}: ${(p * 100).toFixed(2)}%`).join(', ') : ''}</p>
                  </div>
                )
              } : fileData.isJsonL ? {
                expandedRowRender: (record: any) => (
                  <div style={{ margin: 0 }}>
                    <p><strong>User Details:</strong></p>
                    <pre style={{ padding: '8px', background: '#f5f5f5', borderRadius: '4px', fontSize: '12px', maxHeight: '200px', overflow: 'auto' }}>
                      {JSON.stringify(record.user, null, 2)}
                    </pre>
                  </div>
                )
              } : undefined}
            />
          </Card>

          {/* Chart */}
          <Card
            title={`${fileData.chartType === 'bar' ? 'Bar Chart' : fileData.chartType === 'pie' ? 'Pie Chart' : 'Area Chart'} - ${fileData.fileName}`}
            style={{ marginBottom: '24px' }}
            extra={
              <Dropdown overlay={getExportMenu(fileData.fileName, fileData.chartData)} trigger={['click']}>
                <Button icon={<DownloadOutlined />}>Export Chart</Button>
              </Dropdown>
            }
          >
            <div ref={el => { chartRefs.current[fileData.fileName] = el; }}>
              <ResponsiveContainer width="100%" height={400}>
                {fileData.chartType === 'pie' ? (
                  <PieChart>
                    <Pie
                      data={fileData.chartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ payload, percent }: any) => `${payload?.name ?? ''} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {fileData.chartData.map((entry, idx) => (
                        <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: any, name: any) => [value, name]} />
                    <Legend />
                  </PieChart>
                ) : fileData.chartType === 'bar' ? (
                  <BarChart data={fileData.chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={0}
                    />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey={fileData.isJsonL ? "followers" : "count"}
                      fill="#4299e1"
                      name={fileData.isJsonL ? "Followers" : "Count"}
                    />
                  </BarChart>
                ) : (
                  <AreaChart data={fileData.chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="value" fillOpacity={0.3} />
                  </AreaChart>
                )}
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      ))}
    </Content>
  );
};

export default UC1;